import { useEffect, useMemo, useState } from 'react'
import { Heading, StatusBadge } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import TaskGroup from '../TaskGroup/TaskGroup'
import { Day, DashboardTaskGroup, CalculatedTaskStatus } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { formatDate, getPartOfDay } from 'common/utils'
import { TaskFiltersValue } from '../TaskFilters/TaskFilters'
import { toDosConstants } from 'locale/toDos'
import useTaskGroups, { TaskGroupCursor } from '../../hooks/useTaskGroups'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'

type DayCardProps = {
  day: Day
  filterParams?: TaskFiltersValue
  refetchDailyTasks: () => Promise<any>
}

const getCursor = (taskGroups: DashboardTaskGroup[]): TaskGroupCursor => {
  const lastTaskGroupIndex = taskGroups.length
  const lastTaskGroup = taskGroups[lastTaskGroupIndex - 1]

  return {
    date: lastTaskGroup.dateTime,
    recurringTaskID: lastTaskGroup.recurringTaskID,
  }
}

const DayCard = ({ day, filterParams, refetchDailyTasks }: DayCardProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)

  const [dayTaskGroups, setDayTakGroups] = useState(day.taskGroups)

  const [usedTaskGroups, setUsedTaskGroups] = useState(dayTaskGroups)

  const [cursor] = useState(getCursor(day.taskGroups))

  const {
    taskGroups: paginatedTaskGroups,
    isLoading: taskGroupsLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useTaskGroups({
    ...filterParams,
    initialCursor: cursor,
  })

  useEffect(() => {
    setDayTakGroups(day.taskGroups)
  }, [day.taskGroups])

  useEffect(() => {
    setUsedTaskGroups(dayTaskGroups.concat(paginatedTaskGroups))
  }, [dayTaskGroups, paginatedTaskGroups])

  const taskGroupsByPartOfDay = useMemo<{
    [partOfDay: string]: DashboardTaskGroup[]
  }>(() => {
    const groupedTaskGroups: { [partOfDay: string]: DashboardTaskGroup[] } = {}

    const computedTaskGroups = usedTaskGroups

    computedTaskGroups.forEach((taskGroup) => {
      const partOfDay = getPartOfDay(taskGroup.dateTime)
      if (!groupedTaskGroups[partOfDay]) {
        groupedTaskGroups[partOfDay] = []
      }
      groupedTaskGroups[partOfDay].push(taskGroup)
    })

    return groupedTaskGroups
  }, [usedTaskGroups])

  const loadMoreTaskGroups = () => {
    fetchNextPage()
  }

  const hasMoreTaskGroups = () => {
    if (paginatedTaskGroups) {
      return day.hasMoreTodos && hasNextPage
    }

    return day.hasMoreTodos
  }

  const generalRefetch = async () => {
    await refetchDailyTasks()
    await refetch()
  }

  return (
    <Wrapper>
      <ToDoHeader>
        <DateContainer>
          <DateText level={3}>{dayjs(day.date).format('D MMMM YYYY')}</DateText>
          <DayText level={1}>{formatDate(dayjs(day.date))}</DayText>
        </DateContainer>
        <StatusesContainer>
          {sortedCounts
            .filter((status) => Object.keys(day.counts).includes(status))
            .map((statusType) => (
              <StatusBadgeStyled
                key={statusType}
                status={statusType as CalculatedTaskStatus}
                count={day.counts[statusType as CalculatedTaskStatus]}
                leftText={false}
                showStatus
              />
            ))}
        </StatusesContainer>
      </ToDoHeader>
      <ToDoContent>
        {Object.entries(taskGroupsByPartOfDay).map(
          ([partOfDay, taskGroups]) => (
            <div key={partOfDay}>
              <SubtitleContainer>
                <Subtitle level={4} weight="bold">
                  {partOfDay}
                </Subtitle>
              </SubtitleContainer>
              <TaskGroupsWrapper>
                {taskGroups.map((taskGroup) => (
                  <TaskGroup
                    key={`${taskGroup.recurringTaskID}-${taskGroup.dateTime}`}
                    taskGroup={taskGroup}
                    filterParams={filterParams}
                    refetchDailyTasks={generalRefetch}
                  />
                ))}
              </TaskGroupsWrapper>
            </div>
          )
        )}
      </ToDoContent>
      {hasMoreTaskGroups() && (
        <LoadMoreWrapper>
          <Button
            isLoading={taskGroupsLoading}
            onClick={loadMoreTaskGroups}
            variant="text"
          >
            {t(toDosConstants.LOAD_MORE)}
          </Button>
        </LoadMoreWrapper>
      )}
    </Wrapper>
  )
}

const sortedCounts: CalculatedTaskStatus[] = [
  CalculatedTaskStatus.pending,
  CalculatedTaskStatus.due,
  CalculatedTaskStatus.overdue,
  CalculatedTaskStatus.completed,
  CalculatedTaskStatus.missed,
  CalculatedTaskStatus.skipped,
]

const TaskGroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StatusBadgeStyled = styled(StatusBadge)`
  padding: 4px 8px;
`

const LoadMoreWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
`

const Wrapper = styled.div`
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 20px;
  }
`

const ToDoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const DateText = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DayText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Subtitle = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SubtitleContainer = styled.div`
  margin-bottom: 12px;
`

const StatusesContainer = styled.div`
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`

const ToDoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export default DayCard
